// src/pages/GalleryUpload.js

import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../styles/GalleryUpload.css";

const MAX_PHOTOS = 20;

const GalleryUpload = () => {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [error, setError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [schoolHashtag, setSchoolHashtag] = useState("");
  const [displayName, setDisplayName] = useState("");
  const fileInputRef = useRef(null);
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!token) return;
      try {
        const response = await axios.get(
          "https://ghrianghraffy.com/auth/profile",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const data = response.data || {};
        const rawName = data.school_name || "";
        const fallbackFromName = rawName ? rawName.replace(/\s+/g, "") : "";

        setSchoolHashtag(
          data.school_hashtag || fallbackFromName || ""
        );
        setDisplayName(data.display_name || "");
      } catch (err) {
        console.error("Failed to fetch profile data:", err);
      }
    };
    fetchProfileData();
  }, [token]);

  const handlePickPhotos = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    const newPhotos = selectedFiles
      .slice(0, MAX_PHOTOS - photos.length)
      .map((file) => ({
        file,
        preview: URL.createObjectURL(file),
        story: "",
      }));

    setPhotos((prev) => [...prev, ...newPhotos].slice(0, MAX_PHOTOS));
    setError("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDeletePhoto = (preview) => {
    setPhotos((prev) => {
      const updated = prev.filter((photo) => photo.preview !== preview);
      URL.revokeObjectURL(preview);
      return updated;
    });
  };

  const handleStoryChange = (preview, value) => {
    setPhotos((prev) =>
      prev.map((photo) =>
        photo.preview === preview ? { ...photo, story: value } : photo
      )
    );
  };

  const canUpload =
    photos.length > 0 &&
    photos.every((photo) => photo.story.trim().length > 0);

  const handleUpload = async () => {
    if (!canUpload) {
      setError("Please add a short story or caption for each photo.");
      return;
    }

    if (!token) {
      setError("You are not authorized. Please log in.");
      navigate("/login");
      return;
    }

    setIsUploading(true);
    setProgress(0);
    setError("");

    try {
      const totalPhotos = photos.length;
      let uploadedPhotos = 0;
      const cleanedDisplayName = (displayName || "").replace(/[\s.]+/g, "");

      for (const photo of photos) {
        const formData = new FormData();
        formData.append("files", photo.file);

        const storyWithHashtags = `${photo.story.trim()} ${
          schoolHashtag || ""
        } ${cleanedDisplayName ? "#" + cleanedDisplayName : ""}`.trim();

        formData.append("stories", storyWithHashtags);

        await axios.post("https://ghrianghraffy.com/photos/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          onUploadProgress: (progressEvent) => {
            if (!progressEvent.total) return;
            const percentCompleted = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            );
            setProgress(
              ((uploadedPhotos + percentCompleted / 100) / totalPhotos) * 100
            );
          },
        });

        uploadedPhotos++;
        setProgress((uploadedPhotos / totalPhotos) * 100);
      }

      setPhotos([]);
      setTimeout(() => navigate("/view-photos"), 1000);
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.response?.data?.detail || "Failed to upload photos.");
    } finally {
      setIsUploading(false);
    }
  };

  const cleanedDisplayName = (displayName || "").replace(/[\s.]+/g, "");

  return (
    <div className="gallery-upload-page">
      <div className="gallery-upload-container">
        <h1 className="gallery-upload-title">Upload existing photos</h1>
        <p className="gallery-upload-subtitle">
          Select up to {MAX_PHOTOS} photos from your laptop or desktop, add a
          short story for each, and we&apos;ll do the rest.
        </p>

        {(schoolHashtag || cleanedDisplayName) && (
          <p className="gallery-upload-hint">
            We&apos;ll automatically add{" "}
            {schoolHashtag && <strong>{schoolHashtag}</strong>}
            {schoolHashtag && cleanedDisplayName && " and "}
            {cleanedDisplayName && <strong>#{cleanedDisplayName}</strong>} to
            each story so they&apos;re easy to find later.
          </p>
        )}

        <div className="file-picker-row">
          <label className="file-input-label">
            <span className="file-input-title">Choose photos</span>
            <span className="file-input-subtitle">
              JPG, PNG – up to {MAX_PHOTOS} at a time
            </span>
            <input
              type="file"
              accept="image/*"
              multiple
              ref={fileInputRef}
              onChange={handlePickPhotos}
              disabled={photos.length >= MAX_PHOTOS || isUploading}
              className="file-input"
            />
          </label>
          <p className="photo-count">
            Selected: <strong>{photos.length}</strong> / {MAX_PHOTOS}
          </p>
        </div>

        {photos.length > 0 && (
          <div className="photo-list">
            {photos.map((photo) => (
              <div key={photo.preview} className="photo-item">
                <div className="photo-thumb-wrapper">
                  <img
                    src={photo.preview}
                    alt={photo.story || "Selected photo"}
                    className="photo-preview"
                  />
                  <button
                    onClick={() => handleDeletePhoto(photo.preview)}
                    className="delete-button"
                    disabled={isUploading}
                    aria-label="Remove this photo"
                  >
                    ×
                  </button>
                </div>
                <textarea
                  value={photo.story}
                  onChange={(e) =>
                    handleStoryChange(photo.preview, e.target.value)
                  }
                  placeholder="Add a short story or caption, plus any extra hashtags…"
                  className="story-input"
                  disabled={isUploading}
                />
              </div>
            ))}
          </div>
        )}

        {photos.length > 0 && (
          <button
            onClick={handleUpload}
            disabled={!canUpload || isUploading}
            className="upload-button"
          >
            {isUploading
              ? `Uploading… ${Math.round(progress)}%`
              : "Upload photos to your school"}
          </button>
        )}

        {isUploading && (
          <div className="progress-bar-container">
            <div className="progress-bar">
              <div
                className="progress"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        )}

        {error && <p className="error-message">{error}</p>}
      </div>
    </div>
  );
};

export default GalleryUpload;
